import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Prioridad } from 'src/app/shared/model/tickets/prioridad.model';
import { Msg } from 'src/app/utilerias/mensajes/Msg';
import { ModalPrioridadComponent } from '../modal-prioridad/modal-prioridad.component';
import { TicketsService } from '../../tickets.service';

@Component({
  selector: 'app-gestion-prioridades',
  standalone: true,
  templateUrl: './gestion-prioridades.component.html',
  imports: [FormsModule, NgIf, NgFor, MatButtonModule],
})
export class GestionPrioridadesComponent implements OnInit {
  listaPrioridades: Prioridad[] = [];
  loading = false;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  currentPage = 1;

  constructor(
    private ticketService: TicketsService,
    private dialog: MatDialog,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.consultarPrioridades();
  }

  consultarPrioridades(): void {
    this.loading = true;
    this.cdr.detectChanges();

    this.ticketService.consultarPrioridad().subscribe({
      next: (res) => {
        this.listaPrioridades = Array.isArray(res?.list) ? [...res.list] : [];
        if (this.listaPrioridades.length === 0) {
          this.msg.warningCall('Sin prioridades para mostrar', null, null);
        }

        const maxPages = this.totalPages;
        if (maxPages > 0 && this.currentPage > maxPages) {
          this.currentPage = maxPages;
        }

        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.listaPrioridades = [];
        this.currentPage = 1;
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  modificarTiempoPrioridad(objectPrioridad: Prioridad): void {
    const dialogRef = this.dialog.open(ModalPrioridadComponent, {
      width: '50%',
      panelClass: 'custom-modalbox',
      data: objectPrioridad,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultarPrioridades();
    });
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.currentPage = 1;
    this.cdr.detectChanges();
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }

    this.currentPage = page;
    this.cdr.detectChanges();
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get paginatedPrioridades(): Prioridad[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.listaPrioridades.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return this.listaPrioridades.length > 0 ? Math.ceil(this.listaPrioridades.length / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.listaPrioridades.length > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.listaPrioridades.length);
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }
}
